import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function DonatePreview() {
  const { t } = useTranslation();
  const headerRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const badgesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("reveal-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );

    if (headerRef.current) {
      headerRef.current.classList.add("reveal");
      observer.observe(headerRef.current);
    }

    if (gridRef.current) {
      const children = Array.from(gridRef.current.children) as HTMLElement[];
      children.forEach((child, i) => {
        child.classList.add("reveal-child");
        child.style.transitionDelay = `${i * 150}ms`;
      });
      observer.observe(gridRef.current);
    }

    if (badgesRef.current) {
      badgesRef.current.classList.add("reveal");
      observer.observe(badgesRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const tiers = [
    {
      amount: "$50",
      labelKey: "donate.tier.supporter",
      impact: "Provides school supplies for 5 students for one semester",
      items: ["Notebooks & pens", "Backpack", "Basic textbooks"],
      color: "border-gray-200 bg-white",
      badge: "",
    },
    {
      amount: "$150",
      labelKey: "donate.tier.champion",
      impact: "Funds a month of clean water access for a family of 6",
      items: ["Water purification tablets", "Storage containers", "Hygiene kit"],
      color: "border-[#4ECDC4] bg-[#4ECDC4]/5",
      badgeKey: "donate.mostPopular",
    },
    {
      amount: "$500",
      labelKey: "donate.tier.patron",
      impact: "Sponsors a girl's full year of secondary school education",
      items: ["Tuition fees", "Uniform & supplies", "Exam registration"],
      color: "border-[#1B2838] bg-[#1B2838]",
      badgeKey: "donate.highImpact",
      dark: true,
    },
  ];

  const trustBadges = [
    { icon: "ri-shield-check-line", labelKey: "donate.trust.charity" },
    { icon: "ri-eye-line", labelKey: "donate.trust.transparent" },
    { icon: "ri-file-text-line", labelKey: "donate.trust.receipts" },
    { icon: "ri-lock-line", labelKey: "donate.trust.secure" },
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12" ref={headerRef}>
          <span className="text-xs font-semibold uppercase tracking-widest text-[#4ECDC4] mb-3 block">
            {t("donate.tag")}
          </span>
          <h2
            className="text-3xl md:text-4xl font-bold text-[#1B2838] mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {t("donate.title")}
          </h2>
          <p className="text-gray-500 text-base max-w-xl mx-auto">{t("donate.subtitle")}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10" ref={gridRef}>
          {tiers.map((tier) => (
            <div
              key={tier.amount}
              className={`rounded-xl border-2 p-7 relative ${tier.color} transition-all duration-300`}
            >
              {tier.badgeKey && (
                <div
                  className={`absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold whitespace-nowrap ${
                    tier.dark ? "bg-[#4ECDC4] text-[#1B2838]" : "bg-[#1B2838] text-white"
                  }`}
                >
                  {t(tier.badgeKey)}
                </div>
              )}
              <div className={`text-4xl font-bold mb-1 ${tier.dark ? "text-white" : "text-[#1B2838]"}`}>
                {tier.amount}
              </div>
              <div className="text-sm font-semibold mb-4 text-[#4ECDC4]">{t(tier.labelKey)}</div>
              <p className={`text-sm leading-relaxed mb-5 ${tier.dark ? "text-gray-300" : "text-gray-600"}`}>
                {tier.impact}
              </p>
              <ul className="space-y-2 mb-6">
                {tier.items.map((item) => (
                  <li
                    key={item}
                    className={`flex items-center gap-2 text-sm ${tier.dark ? "text-gray-300" : "text-gray-500"}`}
                  >
                    <i className="ri-check-line text-[#4ECDC4]"></i>
                    {item}
                  </li>
                ))}
              </ul>
              <Link
                to="/donate"
                className={`block text-center py-3 rounded-full font-bold text-sm whitespace-nowrap cursor-pointer transition-all ${
                  tier.dark
                    ? "bg-[#4ECDC4] text-[#1B2838] hover:bg-[#3dbdb5]"
                    : "bg-[#1B2838] text-white hover:bg-[#2a3a4a]"
                }`}
              >
                {t("donate.donate")} {tier.amount}
              </Link>
            </div>
          ))}
        </div>

        {/* Trust badges */}
        <div
          className="flex flex-wrap justify-center gap-6 pt-8 border-t border-gray-100"
          ref={badgesRef}
        >
          {trustBadges.map((badge) => (
            <div key={badge.labelKey} className="flex items-center gap-2 text-gray-500 text-sm">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#4ECDC4]/10 text-[#4ECDC4]">
                <i className={`${badge.icon} text-base`}></i>
              </div>
              <span>{t(badge.labelKey)}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
